import { ChangeDetectionStrategy, Component, computed, inject, signal, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { UtilityService } from '../../services/utility.service';
import { FormlyErrorComponent } from '../formly-error.component';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { UserDetailModalComponent } from './user-detail-modal.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { debugLog } from '../../utils/debug-logger';
import { classifyEntriesLoadError } from '../../utils/entries-load-error';
import type { EntriesLoadError } from '../../utils/entries-load-error.types';
import { EntriesLoadErrorAlertComponent } from '../entries-load-error-alert/entries-load-error-alert.component';
import { commitUserValue } from './user-edit.util';

@Component({
  selector: 'formly-select-linked-entries',
  standalone: true,
  template: `
    <div class="mb-4" [formlyDataCy]="field">
      <!-- Selected count summary -->
      @if (maxSelections() != null) {
        <div class="text-sm text-accent float-right">
          {{ selectedItems().length }}/{{ maxSelections() }} {{ props.label?.toLowerCase() || 'items' }}
          geselecteerd
        </div>
      }

      @if (props.label) {
        <label class="font-medium inline-block mb-2">
          {{ props.label }} {{ props.required ? '*' : '' }}
          <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
        </label>
      }

      <!-- Loading indicator -->
      @if (isLoading()) {
        <div class="flex justify-center items-center p-8">
          <mat-spinner diameter="36"></mat-spinner>
        </div>
      }

      <!-- Error message. A 403 gets the 'warning' treatment and permission copy instead of the
           generic red error — see entries-load-error.ts. The classified case renders through the
           shared alert; the unclassified "missing module data" guard (loadEntries()) keeps its
           own plain fuse-alert since there is no EntriesLoadError value for it. -->
      @if (loadErrorType()) {
        <app-entries-load-error-alert
          class="mb-4"
          [error]="loadErrorType()!"
          [label]="props.label"
          dataCyPrefix="select-linked-entries"
          [failedAlertType]="'error'" />
      } @else if (error()) {
        <fuse-alert class="mb-4" [type]="'error'" [showIcon]="true" data-cy="select-linked-entries_load-error">
          {{ error() }}
        </fuse-alert>
      }

      <!-- Empty state -->
      @if (!isLoading() && entries().length === 0 && !error() && !loadErrorType()) {
        <fuse-alert class="mb-4" [type]="'info'" [showIcon]="true">
          Geen {{ props.label?.toLowerCase() || 'gekoppelde items' }} gevonden.
        </fuse-alert>
      }

      <!-- Selection limit notice -->
      @if (isOverLimit() && maxSelections() > 0) {
        <fuse-alert class="mb-4" [type]="'warning'" [showIcon]="true">
          Er zijn teveel {{ props.label?.toLowerCase() || 'items' }} geselecteerd. Maximaal aantal selecties is ({{
            maxSelections() || 'onbeperkt'
          }}).
        </fuse-alert>
      }

      <!-- Card grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
        @for (entry of entries(); track entry.id) {
          <div
            class="fuse-card border-2 transition-all relative flex flex-col"
            [class.border-primary]="isSelected(entry)"
            [class.border-opacity-100]="isSelected(entry)"
            [class.border-gray-200]="!isSelected(entry)"
            [class.dark:border-gray-700]="!isSelected(entry)"
            [class.opacity-50]="isOverLimit() && !isSelected(entry)"
            [class.opacity-80]="isDisabled()">
            <!-- Selected badge -->
            @if (isSelected(entry)) {
              <div class="absolute -top-2 -right-2 bg-primary text-on-primary text-xs px-2 py-1 rounded-md shadow-sm z-10">
                {{ isDisabled() ? 'Gekozen' : 'Geselecteerd' }}
              </div>
            }

            <!-- Card content area -->
            <div
              class="flex items-start gap-5 p-4 flex-1 cursor-pointer"
              [class.cursor-pointer]="!isDisabled()"
              data-cy="select-linked-entry"
              (click)="toggleSelection(entry)">
              <!-- Card image or icon -->
              @if (entry.uiState.cardInfo?.image) {
                <div class="flex-shrink-0 w-24 h-24 rounded-full overflow-hidden">
                  <img [src]="entry.uiState.cardInfo?.image" alt="profiel foto" class="w-full h-full object-cover" />
                </div>
              } @else {
                <div class="bg-light rounded-full w-24 h-24 flex items-center justify-center">
                  <mat-icon class="icon-size-16" svgIcon="feather:user"></mat-icon>
                </div>
              }

              <div class="flex-1 min-w-0">
                <div class="font-medium">{{ entry.uiState.cardInfo?.title }}</div>
                @if (entry.uiState.cardInfo?.subTitle) {
                  <div class="text-sm text-accent">{{ entry.uiState.cardInfo?.subTitle }}</div>
                }

                <!-- Additional entry information -->
                @if (entry.uiState.cardInfo?.information?.length > 0) {
                  <div class="mt-2 pt-2 border-t">
                    @for (info of entry.uiState.cardInfo?.information; track info.label) {
                      <div
                        class="text-sm text-accent mt-1.5 w-1/2 float-left flex items-center"
                        [class.w-full]="info.icon == null"
                        [ngClass]="'type-' + info?.label?.toLowerCase()">
                        <!-- Only show icon if available -->
                        @if (info.icon) {
                          <mat-icon class="icon-size-4 mr-1">{{ info.icon }}</mat-icon>
                        }

                        @if (info.key === 'average-rating') {
                          @if (reviewInfo.rating) {
                            <span class="inline-block max-h-32 overflow-auto"
                              >{{
                                reviewInfo.rating === '-' ? reviewInfo.rating : (reviewInfo.rating | number: '1.0-1')
                              }}
                              ({{ reviewInfo.amount }})</span
                            >
                          }
                        } @else if (info.key === 'competencies') {
                          <!-- Show competencies as comma-separated names -->
                          <span class="inline-block text-ellipsis overflow-hidden whitespace-nowrap max-w-full">
                            {{ formatDisplayValue(info.value) }}
                          </span>
                        } @else {
                          <!-- Show value with ellipsis truncation -->
                          @if (isLongField(info.label)) {
                            <span class="line-clamp-2 max-w-full leading-5">{{ formatDisplayValue(info.value) }}</span>
                          } @else {
                            <span class="inline-block text-ellipsis overflow-hidden whitespace-nowrap max-w-full">{{
                              formatDisplayValue(info.value)
                            }}</span>
                          }
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </div>

            <!-- Footer button for profile details -->
            <div
              class="border-t border-gray-200 dark:border-gray-700 text-primary dark:text-primary-400 text-center py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-hover transition-colors select-none rounded-b-2xl"
              (click)="openDetailModal(entry, $event)">
              <span class="text-sm font-medium underline hover:no-underline">Profiel bekijken</span>
            </div>
          </div>
        }
      </div>

      @if (props.description) {
        <div class="mt-4 text-sm text-accent">{{ props.description }}</div>
      }

      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  imports: [
    CommonModule,
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    InfoTooltipComponent,
    MatProgressSpinner,
    FuseAlertComponent,
    FormlyErrorComponent,
    MatBadgeModule,
    MatDialogModule,
    FormlyDataCyDirective,
    EntriesLoadErrorAlertComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectLinkedEntriesComponent extends FieldType implements OnInit {
  private entriesService = inject(EntriesService);
  private modulesStore = inject(ModulesStore);
  private dialog = inject(MatDialog);
  destroy$ = injectDestroy();

  // Reactive state
  error = signal<string | null>(null);
  /** Classification behind `error()`, when it came from the entries load — see entries-load-error.ts. */
  loadErrorType = signal<EntriesLoadError | null>(null);
  isLoading = signal(false);
  entries = signal<Entry[]>([]);

  // Computed values
  linkedModule = computed(() => {
    return this.modulesStore.enabledEntities().find(module => module.code === this.props.linkedModule);
  });

  selectedItems = signal<Array<Entry>>([]);

  maxSelections = computed(() => {
    if (!this.props.maxSelections) return null;
    const maxValue = UtilityService.keyPathToValue({ entry: this.model }, this.props.maxSelections);
    return maxValue || null;
  });

  isOverLimit = computed(() => {
    if (this.maxSelections() === Infinity) return false;
    return this.selectedItems().length > this.maxSelections();
  });
  reviewInfo = { amount: 0, rating: 0 };

  ngOnInit() {
    // Initialize the form control as an array if it's not already
    if (!this.formControl.value) {
      this.formControl.setValue([]);
    } else if (!Array.isArray(this.formControl.value)) {
      this.formControl.setValue([this.formControl.value]);
    }

    this.setCurrentEntries();
    this.loadEntries();
  }

  loadEntries() {
    if (!this.linkedModule()?.id || !this.options.formState?.entry?.id) {
      this.error.set('Kan gekoppelde items niet laden: ontbrekende modulegegevens.');
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);
    this.loadErrorType.set(null);

    // Determine the field to filter on
    const linkedField =
      this.props.linkedField?.includes('entryId') || this.props.linkedField?.includes('my-order-id')
        ? this.props.linkedField
        : this.props.linkedField + '.id';

    // Create filter to get entries linked to current entry
    const filter = `${UtilityService.dotPathToObjectPath(linkedField)} eq '${this.options.formState.entry.id}'`;

    // Load entries
    this.entriesService
      .getEntries({
        moduleId: this.linkedModule().id,
        offset: 0,
        limit: 100,
        filter,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: entries => {
          entries = entries.map(entry => {
            return this.setUiState(entry);
          });
          this.entries.set(entries);
          this.isLoading.set(false);
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error loading linked entries:', err);
          const classified = classifyEntriesLoadError(err);
          debugLog('SelectLinkedEntries', 'entries load failed', {
            linkedModule: this.props.linkedModule,
            status: err.status,
          });
          // `error` stays unset here — the template renders `loadErrorType` through the shared
          // alert; a plain-text `error` copy alongside it would never be shown (F8) and `error`
          // is reserved for the unclassified missing-module-data guard above.
          this.loadErrorType.set(classified);
          this.isLoading.set(false);
        },
      });
  }

  setUiState(entry: Entry): Entry {
    entry.uiState.cardInfo = {
      image: this.getCardImage(entry),
      title: this.getCardTitle(entry),
      subTitle: this.getCardSubtitle(entry),
      information: this.getCardInfo(entry),
    };
    return entry;
  }

  getCardImage(entry: Entry): string | null {
    // Check if entry has a cardInfo image
    if (entry.uiState?.cardInfo?.image) {
      return entry.uiState.cardInfo.image;
    }
    const documentEntry = UtilityService.keyPathToValue(entry, this.props.cardImage);
    if (documentEntry == null) {
      return null;
    }
    if (documentEntry?.publicDocuments != null) {
      return documentEntry.publicDocuments[0]?.blobUrl || null;
    }

    return documentEntry.blobUrl;
  }

  getCardTitle(entry: Entry): string {
    // First check if entry has cardInfo title
    if (entry.uiState?.cardInfo?.title) {
      return entry.uiState.cardInfo.title;
    }

    return UtilityService.keyPathToValue(entry, this.props.cardTitle);
  }

  getCardSubtitle(entry: Entry): string {
    // Check if entry has cardInfo subtitle
    if (entry.uiState?.cardInfo?.subTitle) {
      return entry.uiState.cardInfo.subTitle;
    }

    return UtilityService.keyPathToValue(entry, this.props.cardSubTitle);
  }

  getCardInfo(entry: Entry): Array<{ icon?: string; label?: string; value: any }> {
    // Check if entry has cardInfo information array
    if (entry.uiState?.cardInfo?.information && Array.isArray(entry.uiState.cardInfo.information)) {
      return entry.uiState.cardInfo.information;
    }

    // Create information array from key properties if cardInfo not available
    const infoArray = [];

    // Add additional fields from the props.cardOtherInformation setting
    if (this.props.cardOtherInformation) {
      const additionalFields = Array.isArray(this.props.cardOtherInformation)
        ? this.props.cardOtherInformation
        : [this.props.cardOtherInformation];

      additionalFields.forEach(keyPath => {
        const key = keyPath?.split('.')?.pop();
        let value = UtilityService.keyPathToValue(entry, keyPath);
        if (Array.isArray(value)) {
          value = value.map(v => v.entry);
        }
        if (value == null) {
          value = '-';
        }

        // Only include icon if it's available in the UI state
        const icon = entry.uiState?.cardInfo?.icon || this.keyToIcon(key);

        if (key === 'ratings-amount') {
          this.reviewInfo.amount = typeof value === 'object' ? value.selectedInput || value.name : value;
          return;
        } else if (key === 'average-rating') {
          this.reviewInfo.rating = typeof value === 'object' ? value.selectedInput || value.name : value;
        }

        const infoItem: { icon?: string; label?: string; value: any; key: string } = {
          label: this.formatKeyLabel(key),
          key: key,
          value: typeof value === 'object' && !Array.isArray(value) ? value.selectedInput || value.name : value,
        };

        // Only add icon property if it has a value
        if (icon) {
          infoItem.icon = icon;
        }

        infoArray.push(infoItem);
      });
    }

    UtilityService.sortArrayByProperty(infoArray, 'icon', 'desc');

    return infoArray;
  }

  keyToIcon(key: string): string | null {
    // Map key to icon name
    const iconMap: { [key: string]: string } = {
      id: 'tag',
      entryId: 'description',
      createdAt: 'calendar_today',
      updatedAt: 'update',
      date: 'calendar',
      startDate: 'calendar_today',
      endDate: 'event',
      dueDate: 'event_available',
      status: 'info',
      priority: 'flag',
      assignee: 'person',
      email: 'email',
      phone: 'phone',
      address: 'location_on',
      price: 'euro',
      amount: 'euro',
      rate: 'euro',
      quantity: 'format_list_numbered',
      description: 'description',
      rating: 'star',
      'average-rating': 'star',
      'ratings-amount': 'star',
    };

    return iconMap[key] || null;
  }

  formatKeyLabel(key: string): string {
    // Convert camelCase to Title Case with spaces
    return key
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
  }

  formatDisplayValue(value: any): string {
    if (value == null || value === '-') return '--';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value
          .map(v => {
            if (typeof v === 'string') return v;
            // Handle Taxonomy objects with selectedInput property
            if (v.selectedInput) return v.selectedInput;
            // Handle Entry objects or objects with common display properties
            return v.name || v.title || v.entry?.name || v.entry?.title || v;
          })
          .join(', ');
      }
      // Handle single object with selectedInput (Taxonomy)
      if (value.selectedInput) return value.selectedInput;
      // Handle Entry or other objects
      return value.name || value.title || value.entry?.name || value.entry?.title || JSON.stringify(value);
    }
    return value.toString();
  }

  setCurrentEntries() {
    const currentEntries = this.formControl.value || [];
    this.selectedItems.set(currentEntries);
  }

  isSelected(entry: Entry): boolean {
    return this.selectedItems().some(item => item.id === entry.id);
  }

  toggleSelection(entry: Entry): void {
    if (this.isDisabled()) return;

    // Don't allow new selections if we've reached the limit
    if (!this.isSelected(entry) && this.isOverLimit()) return;

    const currentSelection = [...this.selectedItems()];

    if (this.isSelected(entry)) {
      // Remove from selection
      const newSelection = currentSelection.filter(item => item.id !== entry.id);
      commitUserValue(this.formControl, newSelection.length ? newSelection : []);
    } else {
      // Add to selection
      currentSelection.push(entry);
      commitUserValue(this.formControl, currentSelection);
    }

    this.setCurrentEntries();
  }

  isDisabled(): boolean {
    return this.props.disabled || this.form.disabled;
  }

  /**
   * Determine if field should use multi-line truncation based on label
   */
  isLongField(label: string): boolean {
    if (!label) return false;
    const longFields = ['description', 'omschrijving', 'details', 'opmerking', 'notes', 'bio'];
    return longFields.some(field => label.toLowerCase().includes(field));
  }

  /**
   * Open detail modal for entry
   */
  openDetailModal(entry: Entry, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.dialog.open(UserDetailModalComponent, {
      data: {
        entry: entry,
        title: entry.uiState.cardInfo?.title || 'Profiel Details',
      },
      width: '700px',
      maxHeight: '85vh',
    });
  }
}
